import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useDebounce from '../../hooks/useDebounce';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useCategoriesInfiniteQuery } from '../../hooks/useCategoriesQuery';
import { CategoryResponse } from '../../types/api/categories';
import { CategoryView } from '../categories/components/CategoryView';
import { pageSize } from '../../services/settings';
import { Placeholder, createErrorPlaceholder } from '../basic';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { Stack, TextInput, Button, SkeletonBox } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon } from '@primer/octicons-react'

export default function ForumCategoriesList() {

    const navigate = useNavigate();
    const listRef = useRef<HTMLDivElement | null>(null);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)


    const {
        isLoading: isLoadingAds,
        data: sectionsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useCategoriesInfiniteQuery(
        debouncedSearchQuery, // search
        pageSize, //limit
        {
            enabled: true,
            // ALX-60734: форуму нужны и разделы "только для форума", каталог объявлений их не показывает
            isForumOnly: true
        }
    );

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const handleScroll = useInfiniteScroll(
        listRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        sectionsData
    );

    const openForumsInCategoryPage = (category: CategoryResponse) => {
        navigate('/forums/forum/?id=' + category.id)
    }

    const emptyListDescription = `По вашему запросу не найдено
    подходящих разделов`;

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <div></div>
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />
                    </Stack>
                </Stack>
            }
            content={
                <Stack>

                    <div
                        ref={listRef}
                        onScroll={handleScroll}
                        className="mx-auto max-w-screen-xl overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4"
                    >
                        {sectionsData?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: CategoryResponse, index) => (
                                    <CategoryView
                                        key={e.id}
                                        category={e}
                                        isAllowEdit={false}
                                        isForForum={true}
                                        onCategoryClick={() => openForumsInCategoryPage(e)}
                                        onEditClick={() => { }}
                                        onDeleteClick={() => { }}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    {!isLoadingAds && !isFetchingNextPage && (sectionsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Ничего не было найдено'
                            description={emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoadingAds) &&
                        <div className="max-h-screen overflow-y-auto">
                            <SkeletonBox height="4rem" />
                        </div>
                    }
                </Stack>
            }
        ></RegularPageLayout >
    )

}